import attempt from "lodash/attempt";
import inRange from "lodash/inRange";
import isNil from "lodash/isNil";
import isString from "lodash/isString";
import map from "lodash/map";
import partial from "lodash/partial";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { OperationCancelled, apply, conditionalChaining, continueWhen, notEmpty, notNil, omit } from "../../../../core/utils";
import { createModal, displayConfirm, displayError, displayRejected } from "../../../../core/utils/modals";
import useOnce from "../../../../hooks/use-once";
import usePathNavigate from "../../../../hooks/use-path-navigate";
import useSession from "../../../../hooks/use-session";
import useStorage from "../../../../hooks/use-storage";
import { ActionHandler } from "../../../../types";
import { descargarComprobante as apiDescargarComprobante, descargarComprobantePayworks as apiDescargarComprobantePayworks, descargarReferencia as apiDescargarReferencia, generarReferencia as apiGenerarReferencia, obtenerDatosPayworks as apiObtenerDatosPayworks, obtenerFormasPago as apiObtenerFormasPago, obtenerTokenLigaPagos as apiObtenerTokenLigaPagos, obtenerUrlECheck as apiObtenerUrlECheck, registrarPagoECheck as apiRegistrarPagoECheck, registrarPagoLigaPagos as apiRegistrarPagoLigaPagos, registrarPagoPayworks as apiRegistrarPagoPayworks, validarReferencia as apiValidarReferencia } from "../api/pagos.api";
import { setEnv, startPayment } from "../api/payments";
import DomiciliarPagoModal from "../components/domiciliar-pago.modal";
import ElegirFormaPagoModal from "../components/elegir-forma-pago.modal";
import PagoAutorizadoModal from "../components/pago-autorizado.modal";
import ReferenciaCepInterbancarioModal from "../components/referencia-cep-interbancario.modal";
import ReferenciaCepModal from "../components/referencia-cep.modal";
import ReferenciaCobranzaIntegralModal from "../components/referencia-cobranza-integral.modal";
import { DatosComprobante, DatosReferencia, PagoPendienteRespuesta, RespuestaPayworks, ResumenOperacion } from "../types/pagos.types";

declare const jQuery: any;

const post = (action: string, obj: Record<string, any>) => {
	const form = document.createElement("form");
	form.method = "post";
	form.action = action;

	for (const propertyName in obj) {
		if (obj.hasOwnProperty(propertyName)) {
			const input = document.createElement("input");
			input.name = propertyName;
			input.value = obj[propertyName];
			input.type = "hidden";

			form.appendChild(input);
		}
	}

	document.body.appendChild(form);
	form.submit();
	document.body.removeChild(form);
};

const elegirFormaPago = (formasPago: string[]) =>
	new Promise<number>((resolve, reject) => {
		createModal(ElegirFormaPagoModal, {
			formasPago,
			onAccept: value => resolve(Number(value)),
			onClosed: () => reject(new OperationCancelled())
		});
	});

const generarMensajeValidacion = (pagos: PagoPendienteRespuesta[]) => {
	let mensaje = "";
	const idCobros = pagos.filter(x => notEmpty(x.referencia)).map(x => x.idCobro);
	for (const id of idCobros) {
		mensaje += ` El ID de cobro ${id} ya se encuentra asociado a una referencia. `;
	}
	mensaje += "¿Desea eliminar la referencia(s) de pago asociadas a esta(s) y generar una nueva con la selección realizada?"
	return mensaje;
};

const validarReferencia = async (pagos: PagoPendienteRespuesta[], formaPago: number) => {
	const identificadores = map(pagos, "identificador");
	if (notEmpty(identificadores)) {
		const result = await apiValidarReferencia(identificadores, formaPago)
		if (!result) {
			return displayConfirm(generarMensajeValidacion(pagos), true);
		}
	}
	return true;
};

const desplegarReferencia = (descargarReferencia: ActionHandler<DatosReferencia>, referencia: DatosReferencia) => {
	const modals: Record<string, (props: any) => React.JSX.Element> = {
		"1": ReferenciaCepModal,
		"2": ReferenciaCepInterbancarioModal,
		"3": ReferenciaCobranzaIntegralModal
	};
	const Modal = modals[referencia.tipo.toString()];
	createModal(Modal, { datos: referencia, descargarReferencia: apply(descargarReferencia, referencia) });
};

const desplegarComprobante = (descargarComprobante: ActionHandler<DatosComprobante | undefined>, permiteDomiciliar: boolean, onClosed: VoidFunction) => (comprobante?: DatosComprobante) =>
	createModal(PagoAutorizadoModal, {
		datos: comprobante,
		permiteDomiciliar,
		descargarComprobante: apply(descargarComprobante, comprobante),
		onContinue: domiciliarPago => domiciliarPago && desplegarDomiciliacion(onClosed, comprobante),
		onClosed
	});

export const desplegarDomiciliacion = (onClosed: VoidFunction, comprobante?: DatosComprobante) => {
	comprobante && createModal(DomiciliarPagoModal, {
		numeroReferencia: comprobante.referenciaPago,
		onClosed: onClosed
	});
};

const desplegarRechazado = (error: any) => {
	attempt(jQuery.modal.close);
	displayRejected(error);
};

const programarCierreVentana = (comprobante: DatosComprobante | undefined): Promise<DatosComprobante | undefined> => {
	return new Promise((resolve, _) => {
		attempt(jQuery.modal.close);
		resolve(comprobante);
	});
};

const usePagos = () => {
	const [pagos, setPagos] = useState<PagoPendienteRespuesta[]>([]);
	const [cargandoDatos, setCargandoDatos] = useState(false);
	const { push } = useStorage<PagoPendienteRespuesta[]>();
	const { pop } = useStorage<ResumenOperacion>();
	const mode = useSession.use.eCommerceConfiguration().mode;
	const navigate = usePathNavigate();
	const location = useLocation();

	const descargarReferencia = (datos: DatosReferencia) => {
		setCargandoDatos(true);

		apiDescargarReferencia(datos.referencia)
			.catch(displayError)
			.finally(apply(setCargandoDatos, false));
	};

	const descargarComprobante = (registro?: DatosComprobante) => {
		setCargandoDatos(true);

		continueWhen(isString)(registro?.identificadorPago)
			.then(apiDescargarComprobante)
			.catch(displayError)
			.finally(apply(setCargandoDatos, false));
	};

	const descargarComprobantePayworks = (registro?: DatosComprobante) => {
		setCargandoDatos(true);

		continueWhen(isString)(registro?.referenciaPago)
			.then(apiDescargarComprobantePayworks)
			.catch(displayError)
			.finally(apply(setCargandoDatos, false));
	};

	const pagar = () => {
		setCargandoDatos(true);

		apiObtenerFormasPago()
			.then(elegirFormaPago)
			.then(procesarPago)
			.catch(displayError)
			.finally(apply(setCargandoDatos, false));
	};

	const procesarPago = (formaPago: number) => {
		if (inRange(formaPago, 1, 4)) {
			return generarReferencia(formaPago);
		}

		if (formaPago === 4) {
			return abrirVentanaECheck();
		}

		if (formaPago === 5) {
			return abrirVentanaPayworks();
		}

		if (formaPago === 6) {
			return abrirModalComercioElectronico();
		}
	};

	const generarReferencia = (formaPago: number) => {
		return validarReferencia(pagos, formaPago)
			.then(conditionalChaining)
			.then(partial(apiGenerarReferencia, map(pagos, "identificador"), formaPago))
			.then(partial(desplegarReferencia, descargarReferencia))
			.catch(e => {
				if (e instanceof OperationCancelled) navigate("/:segmento/Pagos");
				throw e;
			});
	};

	const abrirVentanaECheck = () => {
		return apiObtenerUrlECheck(map(pagos, "identificador"))
			.then(url => {
				push("pagosEnviados", pagos);
				window.open(url, "_self", "noopener, noreferrer");
			})
			.catch(displayError);
	};

	const abrirVentanaPayworks = () => {
		return apiObtenerDatosPayworks(map(pagos, "identificador"))
			.then(datos => {
				push("pagosEnviados", pagos);
				post(datos.serviceUrl, omit("serviceUrl")(datos));
			})
			.catch(displayError);
	};

	const abrirModalComercioElectronico = () => {
		setCargandoDatos(true);

		return apiObtenerTokenLigaPagos(map(pagos, "identificador"))
			.then(startPayment)
			.then(apiRegistrarPagoLigaPagos)
			.then(programarCierreVentana)
			.then(desplegarComprobante(descargarComprobante, false, regresar))
			.catch(desplegarRechazado)
			.finally(apply(setCargandoDatos, false));
	};

	const registrarPago = (operacion: ResumenOperacion) => {
		if (operacion.formaPago === 4) {
			registrarPagoECheck(operacion.datos.token);
		}

		if (operacion.formaPago === 5) {
			registrarPagoPayworks(operacion.datos);
		}
	};

	const registrarPagoECheck = (token: string) => {
		setCargandoDatos(true);

		apiRegistrarPagoECheck(token)
			.then(desplegarComprobante(descargarComprobante, true, regresar))
			.catch(displayRejected)
			.finally(apply(setCargandoDatos, false));
	};

	const registrarPagoPayworks = (respuesta: RespuestaPayworks) => {
		setCargandoDatos(true);

		apiRegistrarPagoPayworks(respuesta)
			.then(desplegarComprobante(descargarComprobantePayworks, true, regresar))
			.catch(displayRejected)
			.finally(apply(setCargandoDatos, false));
	};

	const regresar = () => {
		navigate("/:segmento/Pagos")
	};

	useOnce(() => {
		setEnv(mode);

		const estado = location.state as PagoPendienteRespuesta[];
		const operacion = pop("resumenOperacion");

		if (isNil(estado)) {
			regresar();
		} else {
			setPagos(estado);

			if (notNil(operacion)) {
				registrarPago(operacion);
			}
		}
	});

	return {
		cargandoDatos,
		pagos,
		pagar
	};
};

export default usePagos;